import type { AIModel } from '../types.ts/ai_prompts';

export interface ResolvedModel {
  provider: string;
  model_id: string;
}

/**
 * Input shape for resolveFlowStepModels (PLAT-9029).
 * Defined here rather than picking from AIFlowStepQueryResult so this
 * compiles before the DatoCMS migration adds model/fallbackModels fields.
 */
export interface FlowStepModelInput {
  modelName: string;
  model?: AIModel | null;
  fallbackModels?: AIModel[];
}

/**
 * Resolves an AIModel registry record into the {provider, model_id}
 * shape the backend LLM Router expects.
 *
 * Priority: explicit AIModel > prompt-level AIModel > fallback string > undefined (backend default).
 */
export function resolveAIModel(
  explicitModel?: AIModel | null,
  promptModel?: AIModel | null,
  fallbackModelString?: string | null,
): ResolvedModel | undefined {
  if (explicitModel) {
    return {
      provider: explicitModel.providerLink.internalName,
      model_id: explicitModel.modelId,
    };
  }

  if (promptModel) {
    return {
      provider: promptModel.providerLink.internalName,
      model_id: promptModel.modelId,
    };
  }

  if (fallbackModelString) {
    return { provider: 'openai', model_id: fallbackModelString };
  }

  return undefined;
}

/**
 * Resolves model configuration for an AI flow step.
 * Uses new link fields (model + fallbackModels) when present,
 * falls back to legacy modelName comma-separated string.
 *
 * Always returns both legacyModels (for eval'd function compat)
 * and resolvedModels (for provider-aware backend calls).
 */
export function resolveFlowStepModels(
  step: FlowStepModelInput,
): { legacyModels: string[]; resolvedModels: ResolvedModel[] } {
  const legacyModels = step.modelName
    .split(',')
    .map(m => m.trim())
    .filter(Boolean);

  let resolvedModels: ResolvedModel[] = [];
  if (step.model) {
    const primary = resolveAIModel(step.model);
    if (primary) resolvedModels.push(primary);
    if (step.fallbackModels?.length) {
      for (const fb of step.fallbackModels) {
        const resolved = resolveAIModel(fb);
        if (resolved) resolvedModels.push(resolved);
      }
    }
  }
  if (resolvedModels.length === 0) {
    resolvedModels = legacyModels
      .map(m => resolveAIModel(undefined, undefined, m))
      .filter((r): r is ResolvedModel => r !== undefined);
  }

  return { legacyModels, resolvedModels };
}

/**
 * Spreads resolved model into a request body object.
 * Returns empty object if no model resolved (backward compat).
 */
export function spreadModelParams(
  resolved: ResolvedModel | undefined,
): { provider?: string; model_id?: string } {
  if (!resolved) return {};
  return { provider: resolved.provider, model_id: resolved.model_id };
}
